import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import lodash from "lodash";
import { ChatButton } from "./types/chat-button";
import { chatButtonFallback } from "./constants/chat-button-fallback";
import { themeSliceName } from "./constants/theme-slice-name";
import { ChatFrame } from "./types/chat-frame";
import { chatFrameFallback } from "./constants/chat-frame-fallback";
import { ChatMessages } from "./types/chat-messages";
import { chatMessagesFallback } from "./constants/chat-messages-fallback";
import { ChatHeader } from "./types/chat-header";
import { chatHeaderFallback } from "./constants/chat-header-fallback";
import { ChatFooter } from "./types/chat-footer";
import { chatFooterFallback } from "./constants/chat-footer-fallback";
import { Nudge } from "./types/nudge";
import { nudgeFallback } from "./constants/nudge-fallback";
import { PopUp } from "./types/pop-up";
import { popUpFallback } from "./constants/pop-up-fallback";
import { BookAppointmentImplementation } from "./constants/book-appointment-implementation";
import { BookAppointmentDetails } from "./constants/book-appointment-details";
import { CallbackField } from "../../types/dto/app/get-config-file-response-dto";
import { shouldDisplay } from "../app-state/thunks/should-display";

export interface ThemeState {
  themeConfig: {
    chatButton: ChatButton;
    chatFrame: ChatFrame;
    chatHeader: ChatHeader;
    chatMessages: ChatMessages;
    chatFooter: ChatFooter;
    nudge: Nudge;
    popUp: PopUp;
    primaryColor: string,
    secondaryColor: string,

    requireFeedbackAfterUserTurn: number,
    bookAppointmentImplementation: BookAppointmentImplementation;
    bookAppointmentDetails: BookAppointmentDetails | null;
    isOpenBookAppointment: boolean;
    calendlyLink?: string;
    callbackForm?: CallbackField[];
    maxChar: number;
    agentEmail?: string;
  },
}

const initialState: ThemeState = {
  themeConfig: {
    chatButton: chatButtonFallback,
    chatFrame: chatFrameFallback,
    chatHeader: chatHeaderFallback,
    chatMessages: chatMessagesFallback,
    chatFooter: chatFooterFallback,
    nudge: nudgeFallback,
    popUp: popUpFallback,
    primaryColor: 'white',
    secondaryColor: 'black',

    // by default it we show feedback after first user message
    requireFeedbackAfterUserTurn: 1,
    bookAppointmentImplementation: BookAppointmentImplementation.HUBSPOT,
    bookAppointmentDetails: null,
    isOpenBookAppointment: false,
    calendlyLink: '',
    maxChar: 300,
    agentEmail : ''
  },
}

export const themeSlice = createSlice({
  initialState,
  name: themeSliceName,
  reducers: {
    setTheme: (state, action: PayloadAction<ThemeState['themeConfig']>) => {
      const { payload } = action;

      state.themeConfig = payload;
    },
    overrideTheme: (state, action: PayloadAction<ThemeState['themeConfig']>) => {
      const { payload } = action;

      state.themeConfig = lodash.merge(state.themeConfig, payload);
    },
    setBookAppointment: (state, action: PayloadAction<{
      implementation: BookAppointmentImplementation, details?: Partial<BookAppointmentDetails>, calendlyLink?: string,agentEmail?: string
    }>) => {
      const { implementation, details, calendlyLink,agentEmail } = action.payload;

      state.themeConfig.bookAppointmentImplementation = implementation;
      state.themeConfig.bookAppointmentDetails = {
        ...state.themeConfig.bookAppointmentDetails,
        ...details
      } as BookAppointmentDetails;
      state.themeConfig.isOpenBookAppointment = true;

      if (calendlyLink) {
        state.themeConfig.calendlyLink = calendlyLink;
      }
      if (agentEmail) {
        state.themeConfig.agentEmail = agentEmail;
      }

    },
    closeBookAppointment: (state) => {
      state.themeConfig.isOpenBookAppointment = false;
      state.themeConfig.bookAppointmentDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(shouldDisplay.fulfilled, (state, action) => {
      const frontendConfig = action.payload?.frontend_config
      if (!frontendConfig) return;

      const {styles, chat_logo, chat_widget_title, footnote, calendly_link, callbackForm, max_chars , chat_body, chat_footer} = frontendConfig;

      state.themeConfig.calendlyLink = calendly_link;

      state.themeConfig.callbackForm = callbackForm.config;

      if (max_chars) {
        state.themeConfig.maxChar = max_chars;
      }

      if (chat_widget_title) {
        state.themeConfig.chatHeader.title.text = chat_widget_title;
      }
      
      if (chat_logo) {
        state.themeConfig.chatMessages.botMessage.avatar.src = chat_logo;
        state.themeConfig.nudge.botMessage.avatar.src = chat_logo;
      }
      if (chat_body) {
        state.themeConfig.chatMessages.styles = chat_body
      } 
      if (chat_footer && chat_footer.styles){
        state.themeConfig.chatFooter = chat_footer;
      }

      if (styles) {
        state.themeConfig.primaryColor = styles.primary_color;
        state.themeConfig.secondaryColor = styles.secondary_color;
        state.themeConfig.chatMessages.suggestionShips.chip.styles.color = styles.primary_color;
        state.themeConfig.chatButton.openedIcon.styles.background = styles.widget_button_background;
        state.themeConfig.chatHeader.styles.background = styles.header_background;
        state.themeConfig.chatMessages.botMessage.loadingAnimation.styles = {
          ...state.themeConfig.chatMessages.botMessage.loadingAnimation.styles,
          backgroundColor: styles.secondary_color,
          color: styles.primary_color,
        };

        state.themeConfig.chatMessages.botMessage.loadingAnimation.before.styles = {
          ...state.themeConfig.chatMessages.botMessage.loadingAnimation.before.styles,
          backgroundColor: styles.primary_color,
          color: styles.primary_color,
        };
        state.themeConfig.chatMessages.botMessage.loadingAnimation.after.styles = {
          ...state.themeConfig.chatMessages.botMessage.loadingAnimation.after.styles,
          backgroundColor: styles.primary_color,
          color: styles.primary_color,
        };
        if (styles.widget_button_background){
          state.themeConfig.chatButton.styles.background = styles.widget_button_background;
        }
        else {
          state.themeConfig.chatButton.styles.background = styles.primary_color;
        }
        state.themeConfig.chatMessages.botMessage.feedback.feedbackForm.submitButton.styles.backgroundColor = styles.primary_color;
        if (styles.bot_message_background){
          state.themeConfig.chatMessages.botMessage.messageCloud.styles.backgroundColor = styles.bot_message_background;
        }
        if (styles.user_message_background){
          state.themeConfig.chatMessages.userMessage.messageCloud.styles.backgroundColor = styles.user_message_background;
        }
        if (styles.bot_message_text_color){
          state.themeConfig.chatMessages.botMessage.messageCloud.styles.color = styles.bot_message_text_color;
        }
        if (styles.user_message_text_color){
          state.themeConfig.chatMessages.userMessage.messageCloud.styles.color = styles.user_message_text_color;
        }
        state.themeConfig.chatMessages.botMessage.feedback.icons.active.styles.fill = chat_body?.backgroundColor;
        state.themeConfig.chatMessages.sendEmailForm.color = styles.primary_color;
        state.themeConfig.chatMessages.botMessage.feedback.icons.thumbUp.styles.color = styles.primary_color;
        state.themeConfig.chatMessages.botMessage.feedback.icons.thumbDown.styles.color = styles.primary_color;
        state.themeConfig.chatMessages.botMessage.feedback.icons.active.styles.fill = styles.primary_color;
        state.themeConfig.chatMessages.botMessage.feedback.feedbackForm.styles.backgroundColor = styles.user_message_background;
        state.themeConfig.chatMessages.botMessage.feedback.feedbackForm.textarea.styles.background = styles.bot_message_background;
        state.themeConfig.chatMessages.botMessage.feedback.feedbackForm.textarea.styles.color = styles.bot_message_text_color;
        state.themeConfig.chatMessages.botMessage.feedback.icons.thumbUp.styles.color = styles.primary_color;
        state.themeConfig.chatMessages.botMessage.feedback.icons.thumbUp.styles.fill = styles.bot_message_text_color;
        state.themeConfig.chatMessages.botMessage.feedback.icons.active.styles.fill = styles.bot_message_text_color;
        state.themeConfig.chatMessages.botMessage.feedback.icons.active.styles.fill = styles.primary_color;
        if(styles.bot_message_text_color){
          state.themeConfig.chatMessages.botMessage.feedback.icons.outlineColor = styles.bot_message_text_color;
        }
        state.themeConfig.chatMessages.freeform.title.styles.color = styles.bot_message_text_color;

        if (styles.widget_button_icon_opened){
          state.themeConfig.chatButton.openedIcon.src = styles.widget_button_icon_opened;
        }
        if (styles.widget_button_icon_closed){
          state.themeConfig.chatButton.closedIcon.src = styles.widget_button_icon_closed;
        }
        if (styles.header_icon){
          state.themeConfig.chatHeader.icon.src = styles.header_icon;
        }
        if (styles.header_inversion){
          state.themeConfig.chatHeader.inversion = styles.header_inversion;
        }
        state.themeConfig.chatButton.buttonBlur = styles.button_blur
      }

      state.themeConfig.chatFooter.footnotes.innerHtml = footnote || '';
      // if (styles.quick_actions_color){
      //   state.themeConfig.chatFooter.actionButtons.color = styles.quick_actions_color;
      // }
      // else{
      //   state.themeConfig.chatFooter.actionButtons.color = styles.primary_color;
      // }
      // if (styles.quick_actions_background){
      //   state.themeConfig.chatFooter.actionButtons.backgroundColor = styles.quick_actions_background;
      // }
      // else{
      //   state.themeConfig.chatFooter.actionButtons.backgroundColor = 'white';
      // }
    })
  }
});

export const { setTheme, setBookAppointment, closeBookAppointment } = themeSlice.actions;
